/* eslint-disable indent */
import { useEffect, useState, useMemo } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import axios from "axios";
import moment from "moment";
import { useTranslation } from "react-i18next";
import DashboardLayout from "../../Layout/DashboardLayout";
import DashboardTopbar from "../../Layout/DashboardTopbar";
import useTokenBalance from "../../hooks/useTokenBalance";
import {
  API_CREATE_MODEL_URI,
  API_DELETE_MODEL_URI,
  API_UPDATE_MODEL_URI
} from "../../Context/Constants";
import { useApiGenerationContext } from "../../Context/ApiGenerationContext";
import { useEngineConfigContext } from "../../Context/EngineConfigContext";
import { useAlertContext } from "../../Context/AlertContext";
import ApiFormToolbar from "./ApiFormToolbar";
import ApiFormHeader from "./ApiFormHeader";
import ApiFormRecordSelect from "./ApiFormRecordSelect";
import ApiFormRecordTable from "./ApiFormRecordTable";
import ApiFormField from "./ApiFormField";
import ApiFormActions from "./ApiFormActions";
import "../ApiView/api-view.css";
import "./api-form.css";

const RELATION_TYPES = ["one2one", "many2one", "one2many", "many2many"];

const ApiForm = () => {
  const {
    currentModel,
    selectedRecord,
    currentRecord,
    selectedFields,
    setSelectedFields,
    selectedMethod: action,
    fetchAllRecords,
    fetching,
    fields,
    selectedConfig,
    fieldType,
    requiredFields,
    fetchRecords,
    models
  } = useApiGenerationContext();
  const [formValues, setFormValues] = useState({});
  const [chosenRecord, setChosenRecord] = useState({});
  const [allRecords, setAllRecords] = useState([]);
  const [availableRecords, setAvailableRecords] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [allModelRecords, setAllModelRecords] = useState({});
  const { currentConfig } = useEngineConfigContext();
  const { setSuccess, setError } = useAlertContext();
  const { t } = useTranslation("common");
  const navigator = useNavigate();
  const { tokenBalance } = useTokenBalance();

  const mappedFields = useMemo(() => {
    const list = Array.isArray(selectedFields) ? selectedFields : [];
    const fieldMap = fields && typeof fields === "object" ? fields : {};
    return list.reduce((prev, f) => {
      if (!fieldMap[f]) {
        return prev;
      }
      return { ...prev, [f]: fieldMap[f] };
    }, {});
  }, [selectedFields, fields]);

  const safeFields = fields && typeof fields === "object" ? fields : {};

  const model = useMemo(() => {
    return (models || []).find(({ model: modelName }) => modelName === currentModel);
  }, [currentModel, models]);

  useEffect(() => {
    const list = Array.isArray(selectedFields) ? selectedFields : [];
    if (!currentModel || !list.length) {
      return undefined;
    }
    let cancelled = false;
    setSelectedFields(list);
    fetchAllRecords(currentModel, list)
      .then((res) => {
        if (cancelled) return;
        setAllRecords(res || []);
        const options = (res || []).map((rs) => [rs.id, rs.display_name]);
        setAvailableRecords(options);
      })
      .catch((err) => {
        if (!cancelled) {
          console.error("fetchAllRecords failed:", err?.message || err);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [mappedFields]);

  useEffect(() => {
    if (currentRecord) {
      setFormValues(
        Object.keys(mappedFields).reduce(
          (prev, field) => ({ ...prev, [field]: currentRecord[field] }),
          {}
        )
      );
    }
  }, [currentRecord, mappedFields]);

  useEffect(() => {
    const relationModels = Object.values(mappedFields)
      .filter((fieldMeta) => fieldMeta && RELATION_TYPES.includes(fieldMeta.type))
      .map((fieldMeta) => fieldMeta.relation)
      .filter(Boolean);

    if (relationModels.length === 0) {
      return undefined;
    }

    let cancelled = false;
    Promise.all(relationModels.map((modelName) => fetchRecords(modelName)))
      .then((all) => {
        if (cancelled) return;
        setAllModelRecords(
          relationModels.reduce(
            (prev, modelName, index) => ({
              ...prev,
              [modelName]: (all[index] || []).map(({ id, display_name }) => [id, display_name])
            }),
            {}
          )
        );
      })
      .catch((err) => {
        if (!cancelled) {
          console.error("fetchRecords failed:", err?.message || err);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [mappedFields]);

  const handleFormChange = (field, value, type) => {
    let nextValue = value;
    if (type === "datetime") {
      nextValue = moment(value).format("YYYY-MM-DD hh:mm:ss");
    } else if (
      type === "integer" ||
      type === "one2one" ||
      type === "many2one" ||
      type === "one2many" ||
      type === "many2many"
    ) {
      if (Array.isArray(value)) {
        nextValue = value.map((v) => parseInt(v, 10));
      } else {
        nextValue = parseInt(value, 10);
      }
    }
    setFormValues({ ...formValues, [field]: nextValue });
  };

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const p = {
        configName: selectedConfig,
        model: currentModel,
        serviceValidation: true,
        required: requiredFields,
        inputFields: selectedFields,
        properties: fieldType,
        input: formValues
      };
      const resp = await axios.post(API_CREATE_MODEL_URI, p);
      console.log(resp);
      setSuccess("Data created!", true);
      navigator("/api-generator/view");
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const p = {
        configName: selectedConfig,
        model: currentModel,
        serviceValidation: true,
        required: requiredFields,
        inputFields: selectedFields,
        properties: fieldType,
        input: formValues,
        id: selectedRecord ? selectedRecord : t("YOUR_RECORD_ID")
      };
      const resp = await axios.post(API_UPDATE_MODEL_URI, p);
      console.log(resp);
      setSuccess("Data updated!", true);
      navigator("/api-generator");
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleRecordChange = async (id) => {
    const matched = (allRecords || []).find((res) => id == res.id);
    setChosenRecord(matched || {});
  };

  const handleDeleteSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const resp = await axios.post(API_DELETE_MODEL_URI, {
        configName: currentConfig?.name,
        model: currentModel,
        id: selectedRecord
      });
      console.log(resp);
      setSuccess("Data deleted!", true);
      navigator("/api-generator");
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (!(currentModel && action)) {
    return <Navigate to="/api-generator" replace />;
  }

  // Original code used `[("delete", "view")].includes(action)` which evaluates to
  // `["view"].includes(action)` because of the comma operator.
  const showRecordTable = action === "view";

  return (
    <DashboardLayout>
      <div className="dashboard-page api-form-page">
        <DashboardTopbar title={t("Test Api")} tokenBalance={tokenBalance} />

        <div className="page-content">
          <ApiFormToolbar configName={selectedConfig} />

          <div className="page-header">
            <h1>{t("Test Api")}</h1>
            <p>{t("Run the generated API against live Odoo data")}</p>
          </div>

          <form className="form-view-card" onSubmit={(e) => e.preventDefault()}>
            <ApiFormHeader
              action={action}
              modelLabel={model?.display_name}
              modelName={currentModel}
            />

            {action === "view" ? (
              <ApiFormRecordSelect
                options={availableRecords}
                onChange={handleRecordChange}
                disabled={fetching}
              />
            ) : null}

            {fetching ? (
              <div className="api-form-empty">{t("Fetching record...")}</div>
            ) : null}

            {showRecordTable ? (
              <ApiFormRecordTable record={chosenRecord} fields={safeFields} />
            ) : (
              <div className="form-view-body">
                <div className="form-fields-grid">
                  {Object.entries(mappedFields).map(([name, options]) => (
                    <ApiFormField
                      key={name}
                      name={name}
                      options={options || {}}
                      value={formValues[name]}
                      onChange={handleFormChange}
                      relationOptions={allModelRecords?.[options?.relation] || []}
                    />
                  ))}
                </div>
                <ApiFormActions
                  action={action}
                  submitting={submitting}
                  onCreate={handleCreateSubmit}
                  onUpdate={handleUpdateSubmit}
                  onDelete={handleDeleteSubmit}
                />
              </div>
            )}
          </form>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ApiForm;
